import { getMarketLabel, isBinaryPlayerProp, isPlayerPropMarket, isLineRequired, marketKey, marketFieldText } from "../../lib/markets";
import { fmtOdds, hasLineValue } from "../../lib/betting";
import { gamelessLegMessage } from "../../lib/save-blockers";
import { JT } from "../../components/ui/jumbotronTokens";
import GamePicker from "../../components/ui/GamePicker";

// Amber "edited"/warn accent — kept as a literal hex (not a JT token) to match
// the existing edited-badge convention carried over from Task 1 (SlipImporter.jsx).
// Single owner: SlipImporter imports it (UI critique PR 2). 8.4:1 on JT.bg.card.
export const EDIT_ACCENT = "#f59e0b";

// Flagged-row background — kept as the exact legacy hex (not JT.status.missing.dim,
// which is #1c1300, one shade off) because ImportConfirmDetails.test.jsx L70-83
// pins this literal value via inline-style assertion. JT.bg.card doubles as the
// unflagged-row background so a plain row is visually a no-op against the ticket
// card behind it, while still satisfying the same test's "clean leg" background check.
const FLAGGED_BG = "#1a1500";

// Ticket-skin input styles — mirror SlipImporter's Task-1 `ticketNameInput` /
// `ticketMarketInput` / `focusRing` / `blurRing` so parlay leg rows read as the
// same ticket row language as the single-bet ticket rows (spec §4.6).
const ticketNameInput = {width:"100%",background:"transparent",border:"none",borderBottom:`1px dashed ${JT.text.dim}`,outline:"none",padding:"4px 0",color:JT.text.primary,fontSize:14,fontWeight:800,fontFamily:JT.font.display,boxSizing:"border-box"};
const ticketMarketInput = {width:"100%",background:"transparent",border:"none",borderBottom:`1px dashed ${JT.text.dim}`,outline:"none",padding:"4px 0",color:JT.text.tertiary,fontSize:11,fontFamily:"inherit",boxSizing:"border-box"};
const focusRing = e => { e.target.style.outline = `1px solid ${JT.border.mid}`; };
const blurRing = e => { e.target.style.outline = "none"; };

export default function ImportConfirmDetails({ legs, confidenceScore, warningCodes, affectedLegIndices, spreadSignLegIndices, legMessages, onEditLeg, editedFields }) {
  if (!legs || legs.length === 0) return null;

  const affectedSet = new Set(affectedLegIndices || []);
  // #1132 D5.3: editable rows that get the spread sign control (flagged under D5.1).
  const spreadSignSet = new Set(spreadSignLegIndices || []);
  const wc = warningCodes || [];

  return (
    <>
      {/* ── Confidence indicator — mirrors SlipImporter's single-bet ticket badges (Task 1) ── */}
      {confidenceScore === 'HIGH' && (
        <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 10px",background:JT.status.won.dim,border:`1px solid ${JT.status.won.border}`,borderRadius:20,marginTop:10,marginBottom:10}}>
          <span style={{fontSize:11,fontWeight:700,color:JT.status.won.color}}>High confidence</span>
          <span style={{fontSize:11,color:JT.text.secondary}}>This bet will auto-track and settle</span>
        </div>
      )}
      {confidenceScore === 'MEDIUM' && (
        <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 10px",background:JT.status.missing.dim,border:`1px solid ${JT.status.missing.border}`,borderRadius:20,marginTop:10,marginBottom:10}}>
          <span style={{fontSize:11,fontWeight:700,color:JT.status.missing.color}}>Review flagged fields</span>
          <span style={{fontSize:11,color:JT.text.secondary}}>Some details flagged — review before saving</span>
        </div>
      )}
      {confidenceScore === 'LOW' && (
        <div style={{padding:"10px 12px",background:JT.status.live.dim,border:`1px solid ${JT.status.live.border}`,borderRadius:8,marginTop:10,marginBottom:10,fontSize:12,color:JT.text.secondary,lineHeight:1.5}}>
          This bet may need manual settlement — we'll try to auto-track
        </div>
      )}

      {/* ── Leg rows — ticket-row visual language (spec §4.6) ──────────────
          flex row, border-bottom divider between legs, last row has no border. */}
      {legs.map((leg, i) => {
        const propLabel = getMarketLabel(leg) || leg.type || "—";
        const hasLine = hasLineValue(leg.line) && !isBinaryPlayerProp(leg);
        // #807: coerce non-string overUnder to "" — `?.endsWith` only guards
        // null/undefined, so a truthy number/object still throws and crashes
        // the confirm sheet. String-or-empty is behavior-identical for strings.
        const ou = typeof leg.overUnder === "string" ? leg.overUnder : "";
        const lineDisplay = hasLine ? (ou.endsWith("+") ? ` · ${ou}` : ` · ${ou} ${leg.line}`) : "";
        const isAffected = affectedSet.has(i);
        const isFlagged = confidenceScore === 'MEDIUM' && (leg.resolution_warnings || []).some(rw => wc.includes(rw));
        const isLast = i === legs.length - 1;

        const nameEdited = editedFields?.has(`legs[${i}].playerName`) || editedFields?.has(`legs[${i}].player`);
        const pickEdited = editedFields?.has(`legs[${i}].pick`);
        const typeEdited = editedFields?.has(`legs[${i}].type`) || editedFields?.has(`legs[${i}].market`);
        const ouEdited = editedFields?.has(`legs[${i}].overUnder`);
        const lineEdited = editedFields?.has(`legs[${i}].line`) || editedFields?.has(`legs[${i}].lineSign`);

        return (
          <div key={i} style={{
            display:"flex",
            flexDirection:"row",
            justifyContent:"space-between",
            alignItems:"flex-start",
            gap:10,
            padding: isFlagged ? "12px 0 12px 8px" : "12px 0",
            borderBottom: isLast ? "none" : `1px solid ${JT.border.default}`,
            background: isFlagged ? FLAGGED_BG : JT.bg.card,
            borderLeft: isFlagged ? `3px solid ${JT.status.missing.color}` : undefined,
            borderRadius: isFlagged ? 6 : undefined,
            width:"100%",
          }}>
            <div style={{flex:1,minWidth:0}}>
            <div style={{...JT.type.label,fontWeight:800,color:JT.text.tertiary,marginBottom:4,letterSpacing:"1px"}}>{`LEG ${i + 1}`}</div>
            {onEditLeg ? (<>
              {(leg.player || leg.playerName || isPlayerPropMarket(leg)) ? (
                <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:3,borderLeft:nameEdited?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:nameEdited?6:0}}>
                  <input value={leg.playerName??leg.player??''} onChange={e=>onEditLeg(i,'playerName',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={ticketNameInput}/>
                  {nameEdited && <span style={{fontSize:11,color:EDIT_ACCENT,fontWeight:600,whiteSpace:'nowrap'}}>edited</span>}
                </div>
              ) : (
                <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:3,borderLeft:pickEdited?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:pickEdited?6:0}}>
                  <input value={leg.pick??''} onChange={e=>onEditLeg(i,'pick',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={ticketNameInput}/>
                  {pickEdited && <span style={{fontSize:11,color:EDIT_ACCENT,fontWeight:600,whiteSpace:'nowrap'}}>edited</span>}
                </div>
              )}
              <div style={{display:'flex',alignItems:'center',gap:4,marginTop:2,borderLeft:typeEdited?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:typeEdited?6:0}}>
                <input value={marketFieldText(leg.type ?? leg.market)} onChange={e=>onEditLeg(i,'type',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={ticketMarketInput}/>
                {typeEdited && <span style={{fontSize:11,color:EDIT_ACCENT,fontWeight:600,whiteSpace:'nowrap'}}>edited</span>}
              </div>
              {/* leg.league is stamped by the pipeline (Stage B) and preserved by handleEditLeg;
                  the 'nhl' fallback here exists only for legs constructed without it. */}
              {(hasLine || leg.overUnder || (onEditLeg && isPlayerPropMarket(leg) && !isBinaryPlayerProp(leg)) || (onEditLeg && isLineRequired(marketKey(leg), leg.league ?? 'nhl'))) && (
                <div style={{display:'flex',gap:8,marginTop:2}}>
                  <div style={{display:'flex',alignItems:'center',gap:4,borderLeft:ouEdited?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:ouEdited?6:0}}>
                    <select value={leg.overUnder??''} onChange={e=>onEditLeg(i,'overUnder',e.target.value||null)} onFocus={focusRing} onBlur={blurRing} style={{...ticketMarketInput,width:60,appearance:'auto'}}>
                      <option value="">—</option>
                      <option value="Over">Over</option>
                      <option value="Under">Under</option>
                    </select>
                    {ouEdited && <span style={{fontSize:11,color:EDIT_ACCENT,fontWeight:600}}>edited</span>}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:4,borderLeft:lineEdited?`3px solid ${EDIT_ACCENT}`:'none',paddingLeft:lineEdited?6:0}}>
                    {spreadSignSet.has(i) && (
                      <select aria-label="Spread line sign" value={leg.lineSign??''} onChange={e=>onEditLeg(i,'lineSign',e.target.value)} onFocus={focusRing} onBlur={blurRing} style={{...ticketMarketInput,width:60,appearance:'auto'}}>
                        <option value="">Sign</option>
                        <option value="-">-</option>
                        <option value="+">+</option>
                      </select>
                    )}
                    <input type="number" value={leg.line??''} onChange={e=>onEditLeg(i,'line',e.target.value===''?null:Number(e.target.value))} onFocus={focusRing} onBlur={blurRing} style={{...ticketMarketInput,width:50}}/>
                    {lineEdited && <span style={{fontSize:11,color:EDIT_ACCENT,fontWeight:600}}>edited</span>}
                  </div>
                </div>
              )}
              <div style={{fontSize:11,color:JT.text.tertiary,marginTop:2}}>{leg.teams}</div>
              {onEditLeg && <GamePicker candidates={leg.gameCandidates} value={leg.gameId} onChange={(id) => onEditLeg(i, 'gameId', id)} edited={editedFields?.has(`legs[${i}].gameId`)} />}
            </>) : (<>
              <div style={{fontSize:14,fontWeight:800,color:JT.text.primary,fontFamily:JT.font.display}}>{leg.player || leg.playerName || leg.pick || "—"}</div>
              <div style={{fontSize:11,color:JT.text.tertiary,marginTop:1}}>{propLabel}{lineDisplay}</div>
              <div style={{fontSize:11,color:JT.text.tertiary,marginTop:1}}>{leg.teams}</div>
            </>)}
            {isAffected && (
              <div style={{fontSize:11,color:JT.text.secondary,marginTop:4,padding:"4px 8px",background:JT.bg.tile,borderRadius:6,border:`1px solid ${JT.border.default}`}}>
                {!leg.gameId
                  ? gamelessLegMessage(leg.resolution_failure_reason)
                  : ((legMessages && legMessages[i]) || 'Review flagged fields before saving')}
              </div>
            )}
            </div>
            {leg.odds != null && leg.odds !== '' && (
              <div style={{...JT.display800,fontSize:18,color:JT.text.secondary,whiteSpace:"nowrap",flexShrink:0,marginTop:16}}>{fmtOdds(leg.odds)}</div>
            )}
          </div>
        );
      })}
    </>
  );
}
